// Localizador de Tiendas Físicas (herramienta de catálogo).
//
// Captura la ubicación del usuario (geolocalización del navegador) y busca
// tiendas de vinilos cercanas, rankeadas de más a menos populares por un agente
// de Gemini sobre datos de Google Places. Cada tienda muestra su valoración,
// distancia y estado (abierta/cerrada) y ofrece abrir las indicaciones de cómo
// llegar en las herramientas de navegación más comunes (Google Maps, Waze,
// Apple Maps). Optimizada para móvil.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useApi } from '../api/ApiContext.js';
import { friendlyError } from '../api/friendlyError.js';
import { useAuth } from '../auth/AuthContext.js';
import { AlertMessage, Badge, ButtonSpinner, Card, Icon, PageHeader } from '../ui/components.js';
import { useToast } from '../ui/ToastProvider.js';
import type { NearbyStore } from '../types.js';

/** Rango permitido del radio (coincide con el backend: MIN/MAX_SEARCH_RADIUS_KM). */
const MIN_RADIUS_KM = 1;
const MAX_RADIUS_KM = 50;

export function StoreLocatorView() {
  const { t } = useTranslation();
  const api = useApi();
  const toast = useToast();
  const { isOwner } = useAuth();
  const [stores, setStores] = useState<NearbyStore[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Radio de búsqueda por defecto del tenant (editable en caliente). Solo el
  // propietario lo ve/edita; el backend revalida el permiso (no es seguridad de
  // frontend). Para el resto de usuarios se usa el valor por defecto del tenant
  // de forma transparente (no se envía radio en la búsqueda).
  const canConfigure = isOwner();
  const [radius, setRadius] = useState<number | null>(null);
  const [savingRadius, setSavingRadius] = useState(false);

  // Carga el radio por defecto del tenant (solo para el propietario).
  useEffect(() => {
    if (!canConfigure) return;
    let active = true;
    void api.storeLocator
      .getConfig()
      .then((cfg) => {
        if (active) setRadius(cfg.search_radius_km);
      })
      .catch(() => {
        /* best-effort: si falla, la búsqueda usa el default del backend */
      });
    return () => {
      active = false;
    };
  }, [api, canConfigure]);

  /** Guarda el nuevo radio por defecto del tenant. */
  async function saveRadius() {
    if (radius === null) return;
    setSavingRadius(true);
    setError(null);
    try {
      const cfg = await api.storeLocator.setConfig(radius);
      setRadius(cfg.search_radius_km);
      toast.success(t('storeLocator.radiusSaved'));
    } catch (err) {
      setError(friendlyError(err, t, 'storeLocator.errSaveRadius'));
    } finally {
      setSavingRadius(false);
    }
  }

  /**
   * Flujo completo: pide la ubicación al navegador y luego consulta el backend.
   * La geolocalización requiere consentimiento del usuario y un contexto seguro
   * (HTTPS). Si se deniega o no está disponible, se informa con un mensaje claro.
   */
  async function locate() {
    setError(null);
    setStores(null);
    if (!('geolocation' in navigator)) {
      setError(t('storeLocator.errNoGeolocation'));
      return;
    }
    setBusy(true);
    try {
      const position = await getCurrentPosition();
      const { latitude, longitude } = position.coords;
      const res = await api.storeLocator.find({
        lat: latitude,
        lng: longitude,
        // Si el propietario ajustó el radio en esta sesión, se envía; en otro
        // caso el backend aplica el radio por defecto del tenant.
        ...(canConfigure && radius !== null ? { radius_km: radius } : {}),
      });
      setStores(res.items);
    } catch (err) {
      setError(resolveError(t, err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="pb-space-xl">
      <PageHeader
        title={t('storeLocator.pageTitle')}
        subtitle={t('storeLocator.pageSubtitle')}
        back
      />

      <div className="px-margin-mobile py-space-md flex flex-col gap-space-md">
        <Card>
          <p className="font-body-sm text-body-sm text-on-surface-variant mb-space-sm">
            {t('storeLocator.intro')}
          </p>
          <button
            type="button"
            onClick={() => void locate()}
            disabled={busy}
            className="w-full flex items-center justify-center gap-space-xs rounded-lg bg-primary text-on-primary font-body-md text-body-md py-space-sm active:scale-95 transition-transform disabled:opacity-60"
          >
            {busy ? <ButtonSpinner /> : <Icon name="my_location" className="text-[20px]" />}
            {busy ? t('storeLocator.searching') : t('storeLocator.findButton')}
          </button>
        </Card>

        {canConfigure && radius !== null && (
          <Card title={t('storeLocator.settingsTitle')}>
            <label className="flex items-center justify-between gap-space-sm">
              <span className="font-body-sm text-body-sm text-on-surface-variant">
                {t('storeLocator.radiusLabel')}
              </span>
              <span className="font-body-md text-body-md text-on-surface font-semibold">
                {t('storeLocator.distanceKm', { km: radius })}
              </span>
            </label>
            <input
              type="range"
              min={MIN_RADIUS_KM}
              max={MAX_RADIUS_KM}
              step={1}
              value={radius}
              onChange={(e) => setRadius(Number(e.target.value))}
              className="w-full mt-space-xs accent-primary"
              aria-label={t('storeLocator.radiusLabel')}
            />
            <p className="font-body-sm text-body-sm text-on-surface-variant mt-space-xs">
              {t('storeLocator.radiusHelp')}
            </p>
            <button
              type="button"
              onClick={() => void saveRadius()}
              disabled={savingRadius}
              className="mt-space-sm flex items-center justify-center gap-space-xs rounded-lg bg-surface-rack text-on-surface font-body-sm text-body-sm px-space-md py-space-xs active:scale-95 transition-transform disabled:opacity-60"
            >
              {savingRadius && <ButtonSpinner />}
              {t('storeLocator.saveRadius')}
            </button>
          </Card>
        )}

        <AlertMessage message={error} />

        {stores && stores.length === 0 && !busy && (
          <AlertMessage message={t('storeLocator.noResults')} tone="warning" />
        )}

        {stores && stores.length > 0 && (
          <div className="flex flex-col gap-space-sm">
            {stores.map((store, index) => (
              <StoreCard key={store.id} store={store} rank={index + 1} t={t} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Tarjeta de una tienda con sus señales y los botones de navegación. */
function StoreCard({ store, rank, t }: { store: NearbyStore; rank: number; t: TFunction }) {
  return (
    <Card>
      <div className="flex items-start gap-space-sm">
        <div className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-surface-rack text-primary font-body-md text-body-md font-bold">
          {rank}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-body-md text-body-md text-on-surface font-semibold truncate">
            {store.name}
          </h3>
          {store.address && (
            <p className="font-body-sm text-body-sm text-on-surface-variant mt-0.5 break-words">
              {store.address}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-space-xs mt-space-xs">
            {store.rating != null && (
              <Badge tone="warn">
                <Icon name="star" className="text-[14px]" /> {store.rating.toFixed(1)}
                {store.user_ratings_total != null && (
                  <span className="opacity-80"> ({store.user_ratings_total})</span>
                )}
              </Badge>
            )}
            {store.distance_meters != null && (
              <Badge tone="neutral">{formatDistance(store.distance_meters, t)}</Badge>
            )}
            {store.open_now != null && (
              <Badge tone={store.open_now ? 'success' : 'danger'}>
                {store.open_now ? t('storeLocator.openNow') : t('storeLocator.closedNow')}
              </Badge>
            )}
          </div>

          {store.highlight && (
            <p className="font-body-sm text-body-sm text-on-surface-variant italic mt-space-xs">
              “{store.highlight}”
            </p>
          )}

          <div className="flex flex-wrap items-center gap-space-xs mt-space-sm">
            <NavLink
              href={googleMapsUrl(store)}
              icon="map"
              label={t('storeLocator.openInMaps')}
            />
            <NavLink href={wazeUrl(store)} icon="navigation" label={t('storeLocator.openInWaze')} />
            <NavLink
              href={appleMapsUrl(store)}
              icon="pin_drop"
              label={t('storeLocator.openInApple')}
            />
            {store.website && (
              <NavLink href={store.website} icon="language" label={t('storeLocator.website')} />
            )}
            {store.phone && (
              <NavLink href={`tel:${store.phone}`} icon="call" label={t('storeLocator.call')} />
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

/** Enlace de navegación/acción (abre en una pestaña nueva). */
function NavLink({ href, icon, label }: { href: string; icon: string; label: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-space-xs rounded-lg bg-surface-rack text-on-surface px-space-sm py-1.5 font-body-sm text-body-sm active:scale-95 transition-transform"
    >
      <Icon name={icon} className="text-[16px]" /> {label}
    </a>
  );
}

/** Promesa alrededor de la geolocalización del navegador (callback → async). */
function getCurrentPosition(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 60000,
    });
  });
}

/** Enlace de indicaciones en Google Maps hacia la tienda (por coordenadas). */
function googleMapsUrl(store: NearbyStore): string {
  const dest = `${store.lat},${store.lng}`;
  const q = encodeURIComponent(store.name);
  // `query_place_id` no siempre disponible; usamos coordenadas + nombre.
  return `https://www.google.com/maps/dir/?api=1&destination=${dest}&destination_place_id=${encodeURIComponent(
    store.id,
  )}&travelmode=driving&q=${q}`;
}

/** Enlace de navegación en Waze hacia la tienda (por coordenadas). */
function wazeUrl(store: NearbyStore): string {
  return `https://waze.com/ul?ll=${store.lat},${store.lng}&navigate=yes`;
}

/** Enlace de Apple Maps hacia la tienda (por coordenadas). */
function appleMapsUrl(store: NearbyStore): string {
  const q = encodeURIComponent(store.name);
  return `https://maps.apple.com/?daddr=${store.lat},${store.lng}&q=${q}`;
}

/** Formatea la distancia en m/km de forma legible. */
function formatDistance(meters: number, t: TFunction): string {
  if (meters < 1000) return t('storeLocator.distanceMeters', { meters });
  return t('storeLocator.distanceKm', { km: (meters / 1000).toFixed(1) });
}

/** Traduce un error de geolocalización o de la API a un mensaje de usuario. */
function resolveError(t: TFunction, err: unknown): string {
  // Errores de geolocalización del navegador (tienen `code` 1/2/3).
  if (
    typeof err === 'object' &&
    err !== null &&
    'code' in err &&
    typeof (err as { code: unknown }).code === 'number'
  ) {
    const code = (err as GeolocationPositionError).code;
    if (code === 1) return t('storeLocator.errPermissionDenied');
    if (code === 2) return t('storeLocator.errPositionUnavailable');
    if (code === 3) return t('storeLocator.errTimeout');
  }
  // Errores de la API (incluye 501 store_locator_not_configured → mensaje claro).
  return friendlyError(err, t, 'storeLocator.errFind');
}
